import React, { forwardRef } from 'react';
import { AlertCircle } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helper?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helper,
      leftIcon,
      rightIcon,
      className = '',
      fullWidth = true,
      ...props
    },
    ref
  ) => {
    return (
      <div className={`${fullWidth ? 'w-full' : ''} mb-4`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {props.required && <span className="text-error-500 ml-1" aria-label="required">*</span>}
          </label>
        )}
        <div className="relative rounded-md">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
           aria-label={label || props.placeholder}
           aria-describedby={error ? `${props.id}-error` : helper ? `${props.id}-helper` : undefined}
           aria-invalid={error ? 'true' : 'false'}
            className={`
              block rounded-lg border-gray-300 shadow-sm bg-white/80
              focus:ring-primary-500 focus:border-primary-500 focus:bg-white
              text-sm
              ${error ? 'border-error-500' : 'border-gray-300'}
              ${leftIcon ? 'pl-10' : 'pl-4'}
              ${rightIcon ? 'pr-10' : 'pr-4'}
              ${fullWidth ? 'w-full' : ''}
              py-3
              transition-all duration-200 ease-in-out
              hover:border-gray-400
              placeholder:text-gray-400
              ${className}
            `}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>
       {error && (
         <p id={`${props.id}-error`} className="mt-2 text-sm text-error-600 flex items-center" role="alert">
           <AlertCircle className="h-4 w-4 mr-1" aria-hidden="true" />
           {error}
         </p>
       )}
       {helper && !error && (
         <p id={`${props.id}-helper`} className="mt-2 text-sm text-gray-500">
           {helper}
         </p>
       )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;